import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

import type { DropdownContentOptions, DropdownTriggerElement } from '../types';
import { getAncestorZIndex } from '../utils/getAncestorZIndex';

type ZIndex = DropdownContentOptions['zIndex'];

/**
 * Resolves the z-index for a dropdown menu from the trigger's ancestor stacking
 * context when the trigger element is available. Use when the dropdown is open so
 * the menu can inherit the correct z-index and render above surrounding content.
 *
 * Uses a callback ref so the resolved z-index updates reliably when the trigger
 * is mounted (ref assignment does not trigger re-renders).
 *
 * @param triggerRef - Ref object to keep in sync with the trigger element; the
 *   returned callback will assign the element to triggerRef.current.
 * @param override - When a number is provided, this value is used instead of the
 *   resolved z-index. When undefined, resolves from the trigger's ancestor or
 *   uses 'auto' if the trigger is not yet mounted.
 * @returns A tuple [triggerRefCallback, zIndex]. Attach triggerRefCallback to the
 *   trigger element as ref={triggerRefCallback}. zIndex is the override when
 *   provided, otherwise the resolved z-index or 'auto'.
 */
export function useTriggerZIndex(
  triggerRef: MutableRefObject<DropdownTriggerElement>,
  override: ZIndex = 'auto',
): [(element: DropdownTriggerElement) => void, ZIndex] {
  const [triggerElement, setTriggerElement] =
    useState<DropdownTriggerElement>(null);
  const [zIndex, setZIndex] = useState<ZIndex>(override);

  const triggerRefCallback = useCallback(
    (element: DropdownTriggerElement) => {
      triggerRef.current = element;
      setTriggerElement(element);
    },
    [triggerRef],
  );

  useEffect(() => {
    if (override !== 'auto') {
      setZIndex(override);

      return;
    }

    if (triggerElement) {
      setZIndex(getAncestorZIndex(triggerElement));
    } else {
      setZIndex('auto');
    }
  }, [triggerElement, override]);

  return [triggerRefCallback, zIndex];
}
